import {
  allowedInvoiceTransitionsFor,
  calculateInvoiceLineNet,
  MAX_DUNNING_LEVEL,
  type CreateCreditNoteInput,
  type CreateInvoiceInput,
  type DunInvoiceInput,
  type FinanceGateway,
  type Invoice,
  type InvoiceDocumentStatus,
  type InvoiceLine,
  type InvoiceDunning,
  type InvoiceLineInput,
  type InvoicePayment,
  type InvoiceSource,
  type ListInvoicesInput,
  type RecordPaymentInput,
  type TransitionInvoiceInput,
  type UpdateInvoiceInput,
} from './types';

/**
 * Offline Finance source. Real invoice entities on the legacy two-axis model: an internal
 * proposal number (RS-26-NNNNNN) from creation and a legal invoice number (RE-26-NNNNNN) assigned
 * only at "Rechnung erstellt", plus a payment axis. A Supabase adapter would replace this and draw
 * both numbers from the central number-range RPC.
 */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function computeLine(input: InvoiceLineInput): InvoiceLine {
  // Rabatt gehoert in den Nettobetrag -- er ging bis 09/2026 auf dem Weg in die Rechnung verloren.
  const discountPercent = Math.min(100, Math.max(0, Number(input.discountPercent) || 0));
  return { ...input, discountPercent, netAmount: calculateInvoiceLineNet({ ...input, discountPercent }) };
}

function totalsOf(lines: readonly InvoiceLine[]): { netTotal: number; vatTotal: number; grossTotal: number } {
  const netTotal = round2(lines.reduce((sum, line) => sum + line.netAmount, 0));
  const vatTotal = round2(lines.reduce((sum, line) => sum + line.netAmount * (line.vatRate / 100), 0));
  return { netTotal, vatTotal, grossTotal: round2(netTotal + vatTotal) };
}

function proposalNumber(sequence: number): string {
  return `RS-26-${String(sequence).padStart(6, '0')}`;
}

function invoiceNumber(sequence: number): string {
  return `RE-26-${String(sequence).padStart(6, '0')}`;
}

function creditNoteNumber(sequence: number): string {
  return `GS-26-${String(sequence).padStart(6, '0')}`;
}

function addDays(iso: string, days: number): string {
  const date = new Date(iso);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

/**
 * Kostenstelle im Demo auflösen (Stufe 2b). Supabase denormalisiert Code + Name über den Join;
 * das Demo hat keinen Stamm-Join, spiegelt aber dieselben Vorgaben wie die Migration/der Demo-Stamm,
 * damit pick → speichern → Auswertung auch ohne Server zusammenpasst. Die Id-Form entspricht dem
 * DemoMastersGateway (`${tenantId}-cost-center-${code}`), sodass der Picker denselben Wert liefert.
 */
const DEMO_COST_CENTER_NAMES: Readonly<Record<string, string>> = {
  HANDEL: 'Maschinenhandel', WERKSTATT: 'Werkstatt / Service', ETEIL: 'Ersatzteile', MIETE: 'Vermietung',
};
const COST_CENTER_MARKER = '-cost-center-';
function demoCostCenterId(tenantId: string, code: string): string {
  return `${tenantId}${COST_CENTER_MARKER}${code}`;
}
function resolveDemoCostCenter(costCenterId: string | null | undefined): {
  costCenterId: string | null; costCenterCode: string | null; costCenterName: string | null;
} {
  if (!costCenterId) return { costCenterId: null, costCenterCode: null, costCenterName: null };
  const at = costCenterId.lastIndexOf(COST_CENTER_MARKER);
  const code = at >= 0 ? costCenterId.slice(at + COST_CENTER_MARKER.length) : null;
  return { costCenterId, costCenterCode: code, costCenterName: code ? (DEMO_COST_CENTER_NAMES[code] ?? code) : null };
}

interface SeedInput {
  readonly tenantId: string;
  readonly proposalSeq: number;
  readonly invoiceSeq: number | null;
  readonly documentStatus: InvoiceDocumentStatus;
  readonly paymentStatus: Invoice['paymentStatus'];
  readonly customerNumber: string;
  readonly customerName: string;
  readonly sourceType: InvoiceSource;
  readonly sourceNumber: string;
  readonly lines: readonly InvoiceLineInput[];
  readonly paidAmount?: number;
  readonly issueDate?: string;
  readonly dunningLevel?: number;
  readonly costCenterCode?: string;
}

function seedInvoice(input: SeedInput): Invoice {
  const lines = input.lines.map(computeLine);
  const totals = totalsOf(lines);
  const invoiced = input.invoiceSeq !== null;
  const issueDate = invoiced ? (input.issueDate ?? '2026-07-10') : null;
  const sent = input.documentStatus === 'sent';
  return {
    id: `${input.tenantId}-invoice-${input.proposalSeq}`,
    tenantId: input.tenantId,
    proposalNumber: proposalNumber(input.proposalSeq),
    invoiceNumber: input.invoiceSeq !== null ? invoiceNumber(input.invoiceSeq) : null,
    documentStatus: input.documentStatus,
    paymentStatus: input.paymentStatus,
    customerNumber: input.customerNumber,
    customerName: input.customerName,
    sourceType: input.sourceType,
    sourceNumber: input.sourceNumber,
    kind: 'invoice',
    creditedInvoiceNumber: null,
    creditNoteNumber: null,
    lines,
    ...totals,
    paidAmount: input.paymentStatus === 'paid' ? totals.grossTotal : input.paidAmount ?? 0,
    settledAmount: input.paymentStatus === 'paid' ? totals.grossTotal : input.paidAmount ?? 0,
    payments: [],
    dunnings: [],
    paymentTermDays: 14,
    issueDate,
    // Beispieldaten: Leistungsdatum = Ausstellungsdatum, so wie die alten Belege es gedruckt haben.
    serviceDate: issueDate,
    serviceEndDate: null,
    dueDate: issueDate ? addDays(`${issueDate}T00:00:00.000Z`, 14) : null,
    sentAt: sent ? '2026-07-10T12:00:00.000Z' : null,
    paidAt: input.paymentStatus === 'paid' ? '2026-07-14' : null,
    dunningLevel: input.dunningLevel ?? 0,
    cancelledAt: null,
    cancellationReason: '',
    createdAt: '2026-07-10T09:00:00.000Z',
    ...resolveDemoCostCenter(input.costCenterCode ? demoCostCenterId(input.tenantId, input.costCenterCode) : null),
  };
}

const seeds: readonly Invoice[] = [
  seedInvoice({
    tenantId: 'ftw', proposalSeq: 1, invoiceSeq: 1, documentStatus: 'sent', paymentStatus: 'paid',
    customerNumber: '10001', customerName: 'Werl Industriebedarf GmbH', sourceType: 'service', sourceNumber: '202600131',
    costCenterCode: 'WERKSTATT',
    lines: [
      { kind: 'material', description: 'Hydraulik-Ersatzteile', quantity: 1, unit: 'Pauschale', unitPrice: 240, vatRate: 19 },
      { kind: 'labour', description: 'Arbeitszeit Werkstatt', quantity: 1.5, unit: 'Stunde', unitPrice: 65, vatRate: 19 },
    ],
  }),
  seedInvoice({
    tenantId: 'ftw', proposalSeq: 2, invoiceSeq: 2, documentStatus: 'sent', paymentStatus: 'open', issueDate: '2026-06-20',
    customerNumber: '10002', customerName: 'Bäckerei Haverkamp GmbH', sourceType: 'sales', sourceNumber: 'AU-26-000001',
    costCenterCode: 'HANDEL',
    lines: [{ kind: 'machine', description: 'Elektro-Gabelstapler EFG 220', quantity: 1, unit: 'Stück', unitPrice: 18450, vatRate: 19 }],
  }),
  seedInvoice({
    tenantId: 'ftw', proposalSeq: 3, invoiceSeq: null, documentStatus: 'draft', paymentStatus: 'not_relevant',
    customerNumber: '10001', customerName: 'Werl Industriebedarf GmbH', sourceType: 'order', sourceNumber: '202600132',
    lines: [{ kind: 'service', description: 'Störungsbehebung Toyota 8FBE16T', quantity: 1, unit: 'Pauschale', unitPrice: 280.5, vatRate: 19 }],
  }),
  seedInvoice({
    tenantId: 'stw', proposalSeq: 1, invoiceSeq: null, documentStatus: 'in_review', paymentStatus: 'not_relevant',
    customerNumber: '20001', customerName: 'Spedition Nordwest KG', sourceType: 'order', sourceNumber: '202600205',
    lines: [{ kind: 'freight', description: 'Schwertransport Sondergenehmigung', quantity: 1, unit: 'Pauschale', unitPrice: 1290, vatRate: 19 }],
  }),
];

export class DemoFinanceGateway implements FinanceGateway {
  private readonly invoices: Invoice[] = seeds.map((invoice) => ({ ...invoice, lines: [...invoice.lines] }));
  private readonly nextProposal: Record<string, number> = { ftw: 4, stw: 2, gsl: 1 };
  private readonly nextInvoice: Record<string, number> = { ftw: 3, stw: 1, gsl: 1 };
  private readonly nextCreditNote: Record<string, number> = { ftw: 1, stw: 1, gsl: 1 };

  async listInvoices({ tenantId, documentStatus = 'all' }: ListInvoicesInput): Promise<readonly Invoice[]> {
    return this.invoices
      .filter((invoice) => invoice.tenantId === tenantId)
      .filter((invoice) => documentStatus === 'all' || invoice.documentStatus === documentStatus)
      .slice()
      .sort((left, right) => right.proposalNumber.localeCompare(left.proposalNumber));
  }

  async createInvoice(input: CreateInvoiceInput): Promise<Invoice> {
    if (input.lines.length === 0) throw new Error('Eine Rechnung benötigt mindestens eine Position.');
    // Ein Vorgang bzw. ein Miet-Abrechnungszeitraum darf nur EINMAL in eine Rechnung überführt werden
    // (Doppelrechnung = geschäftsschädigend). Bei Miete ist die sourceNumber periodengenau, damit die
    // Monatsabrechnung (andere Periode) weiter erlaubt bleibt.
    if ((input.sourceType === 'order' || input.sourceType === 'rental') && input.sourceNumber) {
      const existing = this.invoices.find(
        (invoice) => invoice.tenantId === input.tenantId && invoice.sourceType === input.sourceType && invoice.sourceNumber === input.sourceNumber,
      );
      if (existing) {
        const subject = input.sourceType === 'rental' ? 'diesen Abrechnungszeitraum' : 'diesen Vorgang';
        throw new Error(`Für ${subject} besteht bereits eine Rechnung (${existing.invoiceNumber ?? existing.proposalNumber}).`);
      }
    }
    const lines = input.lines.map(computeLine);
    const sequence = this.nextProposal[input.tenantId] ?? 1;
    this.nextProposal[input.tenantId] = sequence + 1;
    const createdAt = new Date().toISOString();
    const invoice: Invoice = {
      id: `${input.tenantId}-invoice-${sequence}-${createdAt}`,
      tenantId: input.tenantId,
      proposalNumber: proposalNumber(sequence),
      invoiceNumber: null,
      documentStatus: 'draft',
      paymentStatus: 'not_relevant',
      customerNumber: input.customerNumber,
      customerName: input.customerName,
      sourceType: input.sourceType,
      sourceNumber: input.sourceNumber,
      kind: 'invoice',
      creditedInvoiceNumber: null,
      creditNoteNumber: null,
      lines,
      ...totalsOf(lines),
      paidAmount: 0,
      settledAmount: 0,
      payments: [],
      dunnings: [],
      paymentTermDays: input.paymentTermDays ?? 14,
      issueDate: null,
      // Pflichtangabe nach § 14 UStG: aus der Quelle vorbelegt, sonst der heutige Tag. Im Entwurf aenderbar.
      serviceDate: input.serviceDate?.trim() || createdAt.slice(0, 10),
      serviceEndDate: input.serviceEndDate?.trim() || null,
      dueDate: null,
      sentAt: null,
      paidAt: null,
      dunningLevel: 0,
      cancelledAt: null,
      cancellationReason: '',
      createdAt,
      ...resolveDemoCostCenter(input.costCenterId),
      language: input.language ?? 'de',
    };
    this.invoices.unshift(invoice);
    return invoice;
  }

  async updateInvoice(input: UpdateInvoiceInput): Promise<Invoice> {
    const index = this.find(input.tenantId, input.invoiceId);
    const current = this.invoices[index];
    if (current.documentStatus !== 'draft') {
      throw new Error('Nur Rechnungsentwürfe können bearbeitet werden – bitte die Rechnung zuerst auf Entwurf zurücksetzen.');
    }
    if (input.lines.length === 0) throw new Error('Eine Rechnung benötigt mindestens eine Position.');
    const lines = input.lines.map(computeLine);
    // Kostenstelle nur ändern, wenn der Aufrufer den Schlüssel führt (undefined = unverändert lassen).
    const costCenter = input.costCenterId !== undefined
      ? resolveDemoCostCenter(input.costCenterId)
      : { costCenterId: current.costCenterId ?? null, costCenterCode: current.costCenterCode ?? null, costCenterName: current.costCenterName ?? null };
    const updated: Invoice = {
      ...current,
      customerNumber: input.customerNumber,
      customerName: input.customerName,
      paymentTermDays: input.paymentTermDays ?? current.paymentTermDays,
      serviceDate: input.serviceDate !== undefined ? (input.serviceDate?.trim() || null) : current.serviceDate,
      serviceEndDate: input.serviceEndDate !== undefined ? (input.serviceEndDate?.trim() || null) : current.serviceEndDate,
      lines,
      ...totalsOf(lines),
      ...costCenter,
    };
    this.invoices[index] = updated;
    return updated;
  }

  async transitionInvoice({ tenantId, invoiceId, toStatus, reason }: TransitionInvoiceInput): Promise<Invoice> {
    const index = this.find(tenantId, invoiceId);
    const current = this.invoices[index];
    if (!allowedInvoiceTransitionsFor(current.documentStatus).includes(toStatus)) {
      throw new Error(`Der Statuswechsel von ${current.documentStatus} zu ${toStatus} ist nicht zulässig.`);
    }
    const today = new Date().toISOString().slice(0, 10);
    let updated: Invoice = { ...current, documentStatus: toStatus };

    if (toStatus === 'invoiced' && !current.invoiceNumber) {
      const sequence = this.nextInvoice[tenantId] ?? 1;
      this.nextInvoice[tenantId] = sequence + 1;
      updated = {
        ...updated,
        invoiceNumber: invoiceNumber(sequence),
        issueDate: today,
        dueDate: addDays(`${today}T00:00:00.000Z`, current.paymentTermDays),
        paymentStatus: 'open',
      };
    } else if (toStatus === 'sent') {
      updated = { ...updated, sentAt: new Date().toISOString() };
    } else if (toStatus === 'cancelled') {
      updated = { ...updated, cancelledAt: new Date().toISOString(), cancellationReason: reason?.trim() ?? '' };
    }

    this.invoices[index] = updated;
    return updated;
  }

  async recordPayment(input: RecordPaymentInput): Promise<Invoice> {
    const { tenantId, invoiceId, amount } = input;
    const index = this.find(tenantId, invoiceId);
    const current = this.invoices[index];
    if (current.paymentStatus !== 'open' && current.paymentStatus !== 'partial') {
      throw new Error('Für diese Rechnung ist keine Zahlung offen.');
    }
    if (!(amount > 0)) throw new Error('Der Betrag muss größer als 0 sein.');

    const heute = new Date().toISOString().slice(0, 10);
    const eintrag: InvoicePayment = {
      id: `${invoiceId}-pay-${current.payments.length + 1}`,
      kind: input.kind ?? 'payment',
      amount: round2(amount),
      valueDate: input.valueDate?.slice(0, 10) || heute,
      method: input.method ?? '',
      note: input.note ?? '',
      recordedAt: new Date().toISOString(),
    };
    const payments = [...current.payments, eintrag];

    // Nur echtes Geld zaehlt als gezahlt; ausgeglichen wird durch alle drei Arten.
    const paidAmount = round2(payments.filter((p) => p.kind === 'payment').reduce((sum, p) => sum + p.amount, 0));
    const settledAmount = round2(payments.reduce((sum, p) => sum + p.amount, 0));
    const fullyPaid = settledAmount >= current.grossTotal - 0.009;

    const updated: Invoice = {
      ...current,
      payments,
      paidAmount,
      settledAmount,
      paymentStatus: fullyPaid ? 'paid' : 'partial',
      paidAt: fullyPaid ? (current.paidAt ?? eintrag.valueDate) : current.paidAt,
    };
    this.invoices[index] = updated;
    return updated;
  }

  async createCreditNote({ tenantId, invoiceId }: CreateCreditNoteInput): Promise<Invoice> {
    const index = this.find(tenantId, invoiceId);
    const original = this.invoices[index];
    if (original.kind !== 'invoice') throw new Error('Nur Rechnungen können gutgeschrieben werden.');
    if (!original.invoiceNumber) throw new Error('Nur ausgestellte Rechnungen können gutgeschrieben werden.');
    if (original.creditNoteNumber) throw new Error('Für diese Rechnung existiert bereits eine Gutschrift.');

    const proposalSeq = this.nextProposal[tenantId] ?? 1;
    this.nextProposal[tenantId] = proposalSeq + 1;
    const creditSeq = this.nextCreditNote[tenantId] ?? 1;
    this.nextCreditNote[tenantId] = creditSeq + 1;
    const gsNumber = creditNoteNumber(creditSeq);
    const today = new Date().toISOString().slice(0, 10);

    // Link + reverse the original's open receivable first, while `index` is still valid.
    this.invoices[index] = {
      ...original,
      creditNoteNumber: gsNumber,
      paymentStatus: original.paymentStatus === 'open' || original.paymentStatus === 'partial' ? 'not_relevant' : original.paymentStatus,
    };

    const creditNote: Invoice = {
      ...original,
      id: `${tenantId}-credit-${creditSeq}-${new Date().toISOString()}`,
      proposalNumber: proposalNumber(proposalSeq),
      invoiceNumber: gsNumber,
      documentStatus: 'invoiced',
      paymentStatus: 'not_relevant',
      kind: 'credit_note',
      creditedInvoiceNumber: original.invoiceNumber,
      creditNoteNumber: null,
      paidAmount: 0,
      settledAmount: 0,
      payments: [],
      dunnings: [],
      issueDate: today,
      dueDate: null,
      sentAt: null,
      paidAt: null,
      dunningLevel: 0,
      cancelledAt: null,
      cancellationReason: '',
      createdAt: new Date().toISOString(),
    };
    this.invoices.unshift(creditNote);
    return creditNote;
  }

  async dunInvoice(input: DunInvoiceInput): Promise<Invoice> {
    const { tenantId, invoiceId } = input;
    const index = this.find(tenantId, invoiceId);
    const current = this.invoices[index];
    if (current.paymentStatus !== 'open' && current.paymentStatus !== 'partial') {
      throw new Error('Nur offene Rechnungen können gemahnt werden.');
    }
    if (current.dunningLevel >= MAX_DUNNING_LEVEL) {
      throw new Error('Die höchste Mahnstufe ist bereits erreicht.');
    }
    const level = current.dunningLevel + 1;
    const heute = new Date().toISOString().slice(0, 10);
    // Die Betraege werden eingefroren -- was gemahnt wurde, bleibt nachvollziehbar.
    const eintrag: InvoiceDunning = {
      id: `${invoiceId}-dun-${level}`,
      level,
      fee: round2(Math.max(0, input.fee ?? 0)),
      interest: round2(Math.max(0, input.interest ?? 0)),
      openAmount: round2(Math.max(0, current.grossTotal - current.settledAmount)),
      issuedDate: heute,
      dueDate: input.dueDate?.slice(0, 10) || heute,
      note: input.note ?? '',
    };
    const updated: Invoice = {
      ...current,
      dunningLevel: level,
      dunnings: [...current.dunnings, eintrag],
    };
    this.invoices[index] = updated;
    return updated;
  }

  private find(tenantId: string, invoiceId: string): number {
    const index = this.invoices.findIndex((invoice) => invoice.id === invoiceId && invoice.tenantId === tenantId);
    if (index < 0) throw new Error('Die Rechnung wurde in diesem Mandanten nicht gefunden.');
    return index;
  }
}

export function createDemoFinanceGateway(): FinanceGateway {
  return new DemoFinanceGateway();
}
